import React, { useCallback, useMemo } from 'react';
import { Box, Flex, Heading, Select, SelectOptionProps, Text, Icon, MenuItemProps, Link } from '@televet/kibble-ui';
import CardTypeTable from './CardTypeTable';
import {
  CardBrand,
  useGetClinicEmployeesForPaymentSettingsQuery,
  useGetPimsPaymentTypesQuery,
} from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  clearSelectedCardBrands,
  PaymentGrouping,
  setPaymentTypesInitialized,
  setPimsPaymentType,
  updatePaymentGrouping,
  updateSelectedEmployee,
} from '../../state/paymentSettingsSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { getZendeskLink } from '../../utils';
import {
  IntegrationSystemDisplayNameType,
  useBitwerxData,
  useIntegrationsProvider,
} from 'shared/providers/IntegrationsProvider';
import sortBy from 'lodash-es/sortBy';
import WritebackSection from './WritebackSection';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsPaymentsEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IWritebackConfigurationProps {
  setHasUnsavedChange: React.Dispatch<React.SetStateAction<boolean>>;
}

const WritebackConfiguration = ({ setHasUnsavedChange }: IWritebackConfigurationProps): JSX.Element => {
  const { primaryIntegrationName } = useIntegrationsProvider();
  const { bitwerxIntegrationId } = useBitwerxData();
  const { currentClinicId, hasAdminRoleAtCurrentClinic } = useClinicUser();
  const dispatch = useAppDispatch();
  const paymentGrouping = useAppSelector((state) => state.paymentSettings.paymentGrouping);
  const singlePaymentType = useAppSelector((state) => state.paymentSettings.defaultPimsPaymentTypeId);
  const employeeId = useAppSelector((state) => state.paymentSettings.employeeId);
  const { trackEvent } = useTracking();

  const { data: paymentTypesData } = useGetPimsPaymentTypesQuery({
    variables: { integrationId: bitwerxIntegrationId || '' },
    skip: !bitwerxIntegrationId || !hasAdminRoleAtCurrentClinic,
  });

  const { data: clinicEmployeeData } = useGetClinicEmployeesForPaymentSettingsQuery({
    variables: { clinicId: currentClinicId || '' },
    skip: !currentClinicId || !hasAdminRoleAtCurrentClinic,
  });

  const paymentGroupingOptions: SelectOptionProps[] = [
    {
      label: 'As a Single Type',
      value: PaymentGrouping.SingleType,
    },
    {
      label: 'As Card Type Used',
      value: PaymentGrouping.CardType,
    },
  ];

  const paymentTypeOptions: MenuItemProps[] = useMemo(() => {
    const options =
      paymentTypesData?.findManyPimsPaymentType
        ?.filter((type) => !type.isDeleted && !type.isInactive)
        ?.map((type) => {
          return {
            label: type.description || type.id,
            value: type.id,
          };
        }) || [];

    const sortedOptions = sortBy(options, (option) => option.label.toLocaleLowerCase().trim());

    if (!sortedOptions.length) {
      sortedOptions.push({
        label: 'No payment types found',
        value: '',
      });
    }

    return sortedOptions;
  }, [paymentTypesData?.findManyPimsPaymentType]);

  const employeeOptions: MenuItemProps[] = useMemo(() => {
    const options =
      clinicEmployeeData?.findManyClinicEmployee
        ?.filter((employee) => !!employee?.pimsId)
        .map((employee) => {
          const name = `${employee.firstName}${employee.lastName ? ' ' + employee.lastName : ''}`;
          return {
            label: name,
            value: employee.id,
          };
        }) || [];

    const sortedOptions = sortBy(options, (option) => option.label.toLocaleLowerCase().trim());

    if (!sortedOptions.length) {
      sortedOptions.push({
        label: 'No employees found',
        value: '',
      });
    }

    return sortedOptions;
  }, [clinicEmployeeData?.findManyClinicEmployee]);

  const handlePaymentGroupingChange = useCallback(
    (selectedOption: SelectOptionProps) => {
      dispatch(updatePaymentGrouping(selectedOption.value as PaymentGrouping));
      if (selectedOption.value === PaymentGrouping.CardType) trackEvent(SettingsPaymentsEvents.CardSelected);
      setHasUnsavedChange(true);

      // clear any specific card type selections if single card selected
      if (selectedOption.value === PaymentGrouping.SingleType) {
        dispatch(setPimsPaymentType({ cardBrand: CardBrand.Amex, paymentTypeId: null }));
        dispatch(setPimsPaymentType({ cardBrand: CardBrand.Discover, paymentTypeId: null }));
        dispatch(setPimsPaymentType({ cardBrand: CardBrand.Mastercard, paymentTypeId: null }));
        dispatch(setPimsPaymentType({ cardBrand: CardBrand.Visa, paymentTypeId: null }));
        dispatch(clearSelectedCardBrands());
        dispatch(setPaymentTypesInitialized(false));
        trackEvent(SettingsPaymentsEvents.SinglePaymentSelected);
      } else {
        trackEvent(SettingsPaymentsEvents.CardSelected);
      }
    },
    [dispatch, setHasUnsavedChange, trackEvent],
  );

  const handleSinglePaymentTypeChange = useCallback(
    (selectedOption) => {
      if (selectedOption.value) {
        dispatch(setPimsPaymentType({ cardBrand: CardBrand.Unknown, paymentTypeId: selectedOption.value }));
        setHasUnsavedChange(true);
        trackEvent(SettingsPaymentsEvents.CardSelected, { card: selectedOption.label });
      }
    },
    [dispatch, setHasUnsavedChange, trackEvent],
  );

  const handleEmployeeChange = useCallback(
    (selectedOption: SelectOptionProps) => {
      dispatch(updateSelectedEmployee(selectedOption.value || null));
      setHasUnsavedChange(true);
      trackEvent(SettingsPaymentsEvents.EmployeeSelected, { employeeId: selectedOption.value });
    },
    [dispatch, setHasUnsavedChange, trackEvent],
  );

  return (
    <Flex direction="column">
      <Flex direction="column" gap={2}>
        <Heading size="sm">Payment Type</Heading>
        <Text
          size="sm"
          variant="subtle"
        >{`This determines under which Payment Type(s) payments taken in Flow will appear in ${primaryIntegrationName}.`}</Text>
      </Flex>
      <Flex direction="column" pt={4}>
        <Select
          label="How Flow Payments Appear"
          value={paymentGrouping}
          size="sm"
          options={paymentGroupingOptions}
          w="240px"
          listProps={{ onSelect: handlePaymentGroupingChange }}
        />

        {paymentGrouping === PaymentGrouping.SingleType ? (
          <Box pt={4}>
            <Select
              label="Payment Type"
              size="sm"
              isInvalid={!singlePaymentType}
              formControlStyle={{ width: 'fit-content' }}
              value={singlePaymentType || undefined}
              options={paymentTypeOptions}
              placeholder="Select"
              w="240px"
              listProps={{ onSelect: handleSinglePaymentTypeChange }}
              useInvalidIcon={true}
              useInvalidText={false}
              useInvalidBorder={false}
              invalidIconColor="warning"
            />
            <Link
              mt={2}
              iconProps={{ name: 'externalLink' }}
              isExternal
              href={getZendeskLink(primaryIntegrationName as IntegrationSystemDisplayNameType)}
              onClick={(): void => trackEvent(SettingsPaymentsEvents.PaymentTypeAddition)}
              size="sm"
            >
              {`How to add a Payment Type in ${primaryIntegrationName}`}
            </Link>
          </Box>
        ) : (
          <CardTypeTable paymentTypes={paymentTypeOptions} setHasUnsavedChange={setHasUnsavedChange} />
        )}
      </Flex>
      <Box mt={8}>
        <Flex direction="column" gap={2} align="flex-start">
          <Heading size="sm">Employee</Heading>
          <Text size="sm" as="p" pb={2} variant="subtle">
            {`This determines the name under which Payments taken in Flow will appear in ${primaryIntegrationName}. Normally, this information is associated with who entered the payment, but Flow requires a single provider to be associated with all payments.`}
          </Text>

          <Select
            size="sm"
            formControlStyle={{ width: 'fit-content' }}
            options={employeeOptions}
            value={employeeId || undefined}
            w="240px"
            placeholder="Select"
            listProps={{ onSelect: handleEmployeeChange }}
            isInvalid={!employeeId}
            useInvalidText={false}
            useInvalidIcon={true}
            useInvalidBorder={false}
            invalidIconColor="warning"
          />

          <Link
            isExternal
            iconProps={{ name: 'externalLink' }}
            href={getZendeskLink(primaryIntegrationName as IntegrationSystemDisplayNameType, true)}
            onClick={(): void => trackEvent(SettingsPaymentsEvents.EmployeeAddition)}
            size="sm"
          >
            {`How to add an Employee in ${primaryIntegrationName}`}
          </Link>
          <Flex gap={1}>
            <Icon name="clock" size="sm" variant="subtle" />
            <Text size="xs" variant="subtle">
              It can take up to 24 hours for new Employees to appear in the list above
            </Text>
          </Flex>
        </Flex>
      </Box>
      <Box mt={8}>
        <WritebackSection />
      </Box>
    </Flex>
  );
};

export default WritebackConfiguration;
